import { Component } from '@theme/component';

/** @extends {Component<{ hours: HTMLElement, minutes: HTMLElement, seconds: HTMLElement }>} */
class WtgSaleCountdown extends Component {
  requiredRefs = ['hours', 'minutes', 'seconds'];
  #timer;
  #deadline = 0;
  #cycleStart = 0;
  #duration = 864000;
  #repeating = false;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  #start() {
    window.clearInterval(this.#timer);
    const isEditor = window.Shopify?.designMode === true;
    const isDemo = this.dataset.preview === 'true' && isEditor;
    const durationSeconds = Number(this.dataset.duration);
    this.#duration = (Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 864) * 1000;
    this.#repeating = this.dataset.mode === 'repeat' || isDemo;

    if (this.#repeating) {
      // One browser keeps the same cycle across refreshes and return visits.
      // Editor previews do not start or change the storefront visitor's cycle.
      const now = Date.now();
      this.#cycleStart ||= now;
      if (!isEditor) {
        const key = 'wtg-countdown:' + this.dataset.countdownKey + ':' + this.#duration;
        try {
          const saved = Number(window.localStorage.getItem(key));
          this.#cycleStart = Number.isFinite(saved) && saved > 0 && saved <= now ? saved : now;
          if (this.#cycleStart !== saved) window.localStorage.setItem(key, String(this.#cycleStart));
        } catch {
          // Continue normally when the browser does not allow local storage.
        }
      }
    } else {
      const value = (this.dataset.endAt || '').trim();
      const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
      this.#deadline = hasTimezone ? Date.parse(value) : NaN;
    }

    this.#tick();
    if (!this.hidden) this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  #tick() {
    const now = Date.now();
    const remaining = this.#repeating
      ? Math.ceil((this.#duration - (Math.max(0, now - this.#cycleStart) % this.#duration)) / 1000)
      : Math.max(0, Math.ceil((this.#deadline - now) / 1000));

    if (!Number.isFinite(remaining) || remaining <= 0) {
      this.hidden = true;
      window.clearInterval(this.#timer);
      return;
    }
    const values = [Math.floor(remaining / 3600), Math.floor(remaining / 60) % 60, remaining % 60];
    [this.refs.hours, this.refs.minutes, this.refs.seconds].forEach((node, index) => {
      const text = String(values[index]).padStart(2, '0');
      if (node.textContent !== text) node.textContent = text;
    });
    this.hidden = false;
  }
}

if (!customElements.get('wtg-sale-countdown')) {
  customElements.define('wtg-sale-countdown', WtgSaleCountdown);
}
